import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// functions

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

import { DataQuery, RucioLocationsType, Site, SiteStorageArea } from '../../utils/types';

// types specifically for this component.
import {
  QueryParameters,
  SelectedRowsType,
} from '../search-catalog/search-catalog-types';

export interface SearchCatalogCAOMState {
  //
  // the retrieved data from the TAP service
  tapData: any[];
  loadingTapData: boolean;
  columns: string[];

  //
  // the filter form state.
  //-------------------------------

  filter: {
    activeSearchTab: 'cone' | 'adql';
    sourceName: string;
    resolverText: string;
    currentlyResolving: boolean;
    sourceNameError: boolean;
    raValue: string;
    decValue: string;
    radiusValue: string;
    namespaceValue: string;
    filenameValue: string;
    projectValue: string;
    collectionValue: string;
    facilityValue: string;
    instrumentValue: string;
    namespaceDisplayed: boolean;
    filenameDisplayed: boolean;
    projectDisplayed: boolean;
    collectionDisplayed: boolean;
    facilityDisplayed: boolean;
    instrumentDisplayed: boolean;
    positionSearch: boolean;
    dptValue: string | undefined;
    filterWindowSize: { width: number; height: number };
  };

  contextMenus: {
    refreshAddMetadata: number;
  };
  selectedQuery: DataQuery | undefined;
  existingQuery: {
    value: number;
    label: string;
  };
  queries: DataQuery[];
  adqlQuery: string;
  adqlError: string;

  errorMessage: string;

  //
  // maintain a list of selected items.
  // --------------------------------------

  selectedItems: {
    //
    // the selected rows from the search-catalog table.
    selectedRows: SelectedRowsType[];

    // true if all items in the result set are selected.
    selectAll: boolean;

    // a list of selected files on the selected-items tab which
    // have been selected again (i.e. ticked) by the user.
    selectedFiles: { namespace: string; filename: string }[];
    //
  };

  // details on the selected tab and search results.
  searchCatalogTab: string;
  searchResults: QueryParameters;

  // the list of ALL the data-product types.
  dataProductTypes: { dataproduct_type: string; num_records: number }[];

  // the list of data-product types found in the search results.
  dptInResults: string[];
  dptRetrieved: boolean;

  // handle paging.
  pageSize: number;

  // Footer/pagination totals per data-product type from get_product_types.
  total: { dpt: string; total: number }[];

  // Totals refined from the last (partial) page of get_data via a partial-page
  // calculation. setTotal merges these values into total so get_product_types cannot
  // overwrite them.
  correctedTotal: { dpt: string; total: number }[];
  pages: number;
  page: number;
  dptFilter: string;
  displayedPage: {
    dptFilter: string;
    page: number;
    pageSize: number;
  };

  // the info panel on the result set.
  infoExpanded: boolean;
  builtQuery: string | undefined;

  // selected-items tab state.
  existingCollection: {
    value: number;
    label: string;
  };
  existingCollectionText: string;
  collections: {
    value: number;
    label: string;
  }[];
  dataWindowSize: {
    width: number;
    height: number;
  };

  // storage areas will go here for now, but needs to be updated to be in the global slice.
  storageAreas: SiteStorageArea[];
  sRaiseAddToCollectionMessage: any;
  //
} // SearchCatalogCAOMState

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: SearchCatalogCAOMState = {
  tapData: [],
  loadingTapData: false,
  columns: [],
  filter: {
    activeSearchTab: 'cone',
    sourceName: '',
    resolverText: 'Uses SIMBAD name resolver',
    currentlyResolving: false,
    sourceNameError: false,
    raValue: '',
    decValue: '',
    radiusValue: '',
    namespaceValue: '',
    filenameValue: '',
    projectValue: '',
    collectionValue: '',
    facilityValue: '',
    instrumentValue: '',
    namespaceDisplayed: false,
    filenameDisplayed: false,
    projectDisplayed: false,
    collectionDisplayed: false,
    facilityDisplayed: false,
    instrumentDisplayed: false,
    positionSearch: true,
    dptValue: undefined,
    filterWindowSize: { width: 0.0, height: 0.0 },
  },
  contextMenus: {
    refreshAddMetadata: 0,
  },
  selectedQuery: undefined,
  existingQuery: {
    value: -1,
    label: '',
  },
  queries: [],
  adqlQuery: 'SELECT TOP 10 * FROM ivoa.ObsCore',
  adqlError: '',
  errorMessage: '',
  selectedItems: {
    selectedRows: [],
    selectAll: false,
    selectedFiles: [],
  },
  searchCatalogTab: '',
  searchResults: { id: '', obs_publisher_did: [] },
  dataProductTypes: [],
  pageSize: 10,
  total: [],
  correctedTotal: [],
  pages: 0,
  page: 0,
  dptFilter: '<all>',
  displayedPage: { dptFilter: '', page: -1, pageSize: -1 },
  dptInResults: [],
  dptRetrieved: false,
  infoExpanded: false,
  builtQuery: undefined,
  existingCollection: { value: -1, label: '' },
  existingCollectionText: '',
  collections: [],
  dataWindowSize: {
    width: 0.0,
    height: 0.0,
  },
  storageAreas: [],
  sRaiseAddToCollectionMessage: undefined,
};

//	--------------------------------------------------------------------------
//
//	F U N C T I O N S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const searchCatalogCAOMSlice = createSlice({
  name: 'searchCatalogCAOM',
  initialState: initialState,
  reducers: {
    //
    addSearchResults: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<QueryParameters>,
    ) => {
      //
      // replace the single result set
      state.searchResults = action.payload;
      state.total = [];
      state.correctedTotal = [];
      state.pages = 0;
      state.page = 0;
      state.dptFilter = '<all>';
      state.dptInResults = [];
      state.dptRetrieved = false;
      state.infoExpanded = false;
      state.builtQuery = undefined;
      state.tapData = [];
      state.loadingTapData = false;
      state.columns = [];
      state.displayedPage = { dptFilter: '', page: -1, pageSize: -1 };
      state.errorMessage = '';
      state.selectedItems.selectAll = false;
      //
    },

    rowCheckedHandler: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ rows: SelectedRowsType[]; ticked: boolean[] }>,
    ) => {
      //
      // first, check that we've got the same number of rows and tick flags.
      if (action.payload.rows.length === action.payload.ticked.length) {
        //
        state.selectedItems.selectedRows = [
          //
          // items already exist in the state array, and have been unticked, so they should be removed.
          ...state.selectedItems.selectedRows.filter((item) => {
            const index: number = action.payload.rows.findIndex(
              (find) => find.obsPublisherDid === item.obsPublisherDid,
            );
            if (index > -1) {
              return action.payload.ticked[index];
            } else {
              return true;
            }
          }),
          ...action.payload.rows.filter((item, index) => {
            //
            // items don't exist in the state array, but have been ticked, so they should be added.
            if (
              state.selectedItems.selectedRows.findIndex(
                (find) => find.obsPublisherDid === item.obsPublisherDid,
              ) === -1 &&
              action.payload.ticked[index] === true
            ) {
              return true;
            } else {
              return false;
            }
            //
          }),
        ];
        //
      }
      //
    },

    setActiveSearchTab: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<'cone' | 'adql'>,
    ) => {
      state.filter.activeSearchTab = action.payload;
    },

    setADQLError: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.adqlError = action.payload;
    },

    setADQLQuery: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.adqlQuery = action.payload;
    },

    setBuiltQuery: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.builtQuery = action.payload;
    },

    setCollectionDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.collectionDisplayed = action.payload;
    },

    setCollectionValue: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string>,
    ) => {
      state.filter.collectionValue = action.payload;
    },

    setCollections: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<
        {
          value: number;
          label: string;
        }[]
      >,
    ) => {
      state.collections = action.payload;
    },

    setColumns: (state: SearchCatalogCAOMState, action: PayloadAction<string[]>) => {
      state.columns = action.payload;
    },

    setContextMenuRefreshAddMetadata: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<number>,
    ) => {
      state.contextMenus.refreshAddMetadata = action.payload;
    },

    setCurrentlyResolving: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.currentlyResolving = action.payload;
    },

    setDataProductTypes: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ dataproduct_type: string; num_records: number }[]>,
    ) => {
      state.dataProductTypes = action.payload;
    },

    setDataWindowSize: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ width: number; height: number }>,
    ) => {
      state.dataWindowSize = action.payload;
    },

    setDecValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.decValue = action.payload;
    },

    setDisplayedPage: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{
        dptFilter: string;
        page: number;
        pageSize: number;
      }>,
    ) => {
      state.displayedPage = action.payload;
    },

    setDPTFilter: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.dptFilter = action.payload;

      // re-calculate the pages and current page for the new filter.
      const total: number =
        state.total.find((find) => find.dpt === action.payload)?.total ?? 0;
      const pages: number = Math.ceil(total / state.pageSize);
      state.pages = pages;
      state.page = state.page >= pages ? Math.max(0, pages - 1) : state.page;
    },

    setDPTInResults: (state: SearchCatalogCAOMState, action: PayloadAction<string[]>) => {
      state.dptInResults = action.payload;
    },

    setDPTRetrieved: (state: SearchCatalogCAOMState, action: PayloadAction<boolean>) => {
      state.dptRetrieved = action.payload;
    },

    setDPTValue: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.filter.dptValue = action.payload;
    },

    setErrorMessage: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },

    setExistingCollection: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ value: number; label: string }>,
    ) => {
      state.existingCollection = action.payload;
    },

    setExistingCollectionText: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string>,
    ) => {
      state.existingCollectionText = action.payload;
    },

    setExistingQuery: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ value: number; label: string }>,
    ) => {
      state.existingQuery = action.payload;
    },

    setFacilityDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.facilityDisplayed = action.payload;
    },

    setFacilityValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.facilityValue = action.payload;
    },

    setFilenameDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.filenameDisplayed = action.payload;
    },

    setFilenameValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.filenameValue = action.payload;
    },

    setFilterWindowSize: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ width: number; height: number }>,
    ) => {
      state.filter.filterWindowSize = action.payload;
    },

    setInfoExpanded: (state: SearchCatalogCAOMState, action: PayloadAction<boolean>) => {
      state.infoExpanded = action.payload;
    },

    setInstrumentDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.instrumentDisplayed = action.payload;
    },

    setInstrumentValue: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string>,
    ) => {
      state.filter.instrumentValue = action.payload;
    },

    setLoadingTapData: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.loadingTapData = action.payload;
    },

    setNamespaceDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.namespaceDisplayed = action.payload;
    },

    setNamespaceValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.namespaceValue = action.payload;
    },

    setPage: (state: SearchCatalogCAOMState, action: PayloadAction<number>) => {
      state.page = action.payload;
    },

    setPages: (state: SearchCatalogCAOMState, action: PayloadAction<number>) => {
      state.pages = action.payload;
    },

    setPageSize: (state: SearchCatalogCAOMState, action: PayloadAction<number>) => {
      const oldPageSize: number = state.pageSize;
      state.pageSize = action.payload;

      // re-calculate the pages and current page.
      const total: number =
        state.total.find((find) => find.dpt === state.dptFilter)?.total ?? 0;
      state.pages = Math.ceil(total / action.payload);

      // keep the same first row visible.
      const firstRow: number = oldPageSize * state.page;
      state.page = Math.floor(firstRow / action.payload);
    },

    setPositionSearch: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.positionSearch = action.payload;
    },

    setProjectDisplayed: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.projectDisplayed = action.payload;
    },

    setProjectValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.projectValue = action.payload;
    },

    setQueries: (state: SearchCatalogCAOMState, action: PayloadAction<DataQuery[]>) => {
      state.queries = action.payload;
    },

    setRadiusValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.radiusValue = action.payload;
    },

    setRAValue: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.raValue = action.payload;
    },

    setResolverText: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.resolverText = action.payload;
    },

    setSearchCatalogTab: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<string | null>,
    ) => {
      state.searchCatalogTab = action.payload ?? '';
    },

    setSearchResults: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<QueryParameters>,
    ) => {
      state.searchResults = action.payload;
    },

    setSelectAll: (state: SearchCatalogCAOMState, action: PayloadAction<boolean>) => {
      state.selectedItems.selectAll = action.payload;
    },

    setSelectedFiles: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ namespace: string; filename: string }[]>,
    ) => {
      state.selectedItems.selectedFiles = action.payload;
    },

    setSelectedQuery: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<DataQuery | undefined>,
    ) => {
      state.selectedQuery = action.payload;
    },

    setSelectedRows: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<SelectedRowsType[]>,
    ) => {
      state.selectedItems.selectedRows = action.payload;
    },

    setSourceName: (state: SearchCatalogCAOMState, action: PayloadAction<string>) => {
      state.filter.sourceName = action.payload;
    },

    setSourceNameError: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<boolean>,
    ) => {
      state.filter.sourceNameError = action.payload;
    },

    setTapData: (state: SearchCatalogCAOMState, action: PayloadAction<any[]>) => {
      state.tapData = action.payload;
    },

    setTotal: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{ dpt: string; total: number }[]>,
    ) => {
      // Apply get_product_types counts, but preserve trusted get_data totals.
      state.total = action.payload.map((item) => {
        const correctedTotal = state.correctedTotal.find(
          (find) => find.dpt === item.dpt,
        )?.total;
        return correctedTotal !== undefined
          ? { dpt: item.dpt, total: correctedTotal }
          : item;
      });

      const selectedTotal: number =
        state.total.find((item) => item.dpt === state.dptFilter)?.total ?? 0;
      state.pages = selectedTotal > 0 ? Math.ceil(selectedTotal / state.pageSize) : 1;
      state.page = state.page >= state.pages ? Math.max(0, state.pages - 1) : state.page;
    },

    setCorrectedTotal: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{
        dptFilter: string;
        total: number;
        pageSize: number;
      }>,
    ) => {
      // Store a trusted get_data total and update footer pagination.
      const dptFilter: string = action.payload.dptFilter;
      const correctedTotal: number = action.payload.total;

      if (state.correctedTotal.find((item) => item.dpt === dptFilter) === undefined) {
        state.correctedTotal = [
          ...state.correctedTotal,
          { dpt: dptFilter, total: correctedTotal },
        ];
      } else {
        state.correctedTotal = state.correctedTotal.map((item) =>
          item.dpt === dptFilter ? { dpt: item.dpt, total: correctedTotal } : item,
        );
      }

      const updateTotal = (item: { dpt: string; total: number }) => {
        if (item.dpt === dptFilter) {
          return { dpt: item.dpt, total: correctedTotal };
        }
        return item;
      };

      if (state.total.find((item) => item.dpt === dptFilter) === undefined) {
        state.total = [...state.total, { dpt: dptFilter, total: correctedTotal }];
      } else {
        state.total = state.total.map(updateTotal);
      }

      const selectedTotal: number =
        state.total.find((item) => item.dpt === state.dptFilter)?.total ?? 0;
      state.pages =
        selectedTotal > 0 ? Math.ceil(selectedTotal / action.payload.pageSize) : 1;
      state.page = state.page >= state.pages ? Math.max(0, state.pages - 1) : state.page;
    },

    updateSelectedFiles: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<{
        addFiles?: {
          namespace: string;
          filename: string;
        }[];
        removeFiles?: {
          namespace: string;
          filename: string;
        }[];
      }>,
    ) => {
      //
      state.selectedItems.selectedFiles = [
        //
        // remove items.
        ...state.selectedItems.selectedFiles.filter(
          (item) =>
            (action.payload.removeFiles?.findIndex(
              (find) =>
                find.namespace === item.namespace && find.filename === item.filename,
            ) ?? -1) === -1,
        ),

        // add items.
        ...(action.payload.addFiles?.filter(
          (item) =>
            state.selectedItems.selectedFiles.findIndex(
              (find) =>
                find.namespace === item.namespace && find.filename === item.filename,
            ) === -1,
        ) ?? []),
      ];
      //
    },
    setRaiseAddToCollectionMessage: (
      state: SearchCatalogCAOMState,
      action: PayloadAction<any>,
    ) => {
      state.sRaiseAddToCollectionMessage = action.payload;
    },
    //
  },
  //extraReducers: (builder) => {
  //  builder
  //    .addCase(initiateDMSearch.fulfilled, (state: FileBrowserState) => {})
  //},
});

// expose the interface.
export const {
  addSearchResults,
  rowCheckedHandler,
  setActiveSearchTab,
  setBuiltQuery,
  setCollectionDisplayed,
  setCollectionValue,
  setColumns,
  setContextMenuRefreshAddMetadata,
  setCurrentlyResolving,
  setDataProductTypes,
  setDataWindowSize,
  setDecValue,
  setDisplayedPage,
  setDPTFilter,
  setDPTInResults,
  setDPTRetrieved,
  setDPTValue,
  setErrorMessage,
  setExistingCollection,
  setExistingCollectionText,
  setExistingQuery,
  setFacilityDisplayed,
  setFacilityValue,
  setFilenameDisplayed,
  setFilenameValue,
  setFilterWindowSize,
  setInfoExpanded,
  setInstrumentDisplayed,
  setInstrumentValue,
  setLoadingTapData,
  setNamespaceDisplayed,
  setNamespaceValue,
  setPage,
  setPages,
  setPageSize,
  setPositionSearch,
  setProjectDisplayed,
  setProjectValue,
  setQueries,
  setRAValue,
  setRadiusValue,
  setResolverText,
  setSearchCatalogTab,
  setSearchResults,
  setSelectAll,
  setSelectedFiles,
  setSelectedQuery,
  setSelectedRows,
  setSourceName,
  setSourceNameError,
  setTapData,
  setTotal,
  setCorrectedTotal,
  updateSelectedFiles,
  setRaiseAddToCollectionMessage,
} = searchCatalogCAOMSlice.actions;
export default searchCatalogCAOMSlice.reducer;
